// v4 (D7): the `highlight` directive how_do_i returns: go to the screen (when it names one), then find the control and
// flash it, so "how do I set a spending limit?" ends with the eye on the right place. The target is a data-help id
// (data-help="settings.spending") or a CSS selector already on the page ("#packs-h", ".render-btn"). The screen may
// still be loading, so the lookup retries for a couple of seconds; only a visible match counts (the rail and the
// phone tabs both carry nav ids). A heading flashes its whole section. Nothing found: nothing happens.
import { setFold } from "./fold";
import { navigate } from "./router";

export type HighlightDirective = { type: "highlight"; actionId?: string; selector?: string; path?: string; label?: string };

const SAFE_ID = /^[a-z0-9][a-z0-9._-]{0,63}$/i;
const SAFE_SELECTOR = /^[#.][A-Za-z][\w-]{0,63}$/;

/** The CSS selector for a directive's target, or null when it names nothing usable (checked: no arbitrary selectors). */
export function targetSelector(d: Pick<HighlightDirective, "actionId" | "selector">): string | null {
  if (d.actionId && SAFE_ID.test(d.actionId)) return `[data-help="${d.actionId}"]`;
  if (d.selector && SAFE_SELECTOR.test(d.selector)) return d.selector;
  return null;
}

/** A same-app path to go to first (never /api, never another origin). */
export function safePath(p: unknown): string | null {
  return typeof p === "string" && /^\/(?!\/)[\w\-./?=&%:]*$/.test(p) && !p.startsWith("/api/") ? p : null;
}

const visible = (el: Element) => (el as HTMLElement).getClientRects().length > 0;

/** The element to flash: the first visible match; a heading stands for its section. */
export function findTarget(root: ParentNode, selector: string): HTMLElement | null {
  const el = [...root.querySelectorAll(selector)].find(visible) as HTMLElement | undefined;
  if (!el) return null;
  if (/^H[1-6]$/.test(el.tagName)) return (el.closest("section, fieldset, .group") as HTMLElement | null) ?? el;
  return el;
}

/** The name a directive points at, as a Fold lists it in `holds` ("settings.spending", "#mem-h"). */
export function heldName(d: Pick<HighlightDirective, "actionId" | "selector">): string | null {
  if (d.actionId && SAFE_ID.test(d.actionId)) return d.actionId;
  if (d.selector && SAFE_SELECTOR.test(d.selector)) return d.selector;
  return null;
}

/** v4 (V4-P2): the visible fold (components/Fold.tsx `holds`) that holds the target, open or shut. */
export function holdingFold(root: ParentNode, name: string): HTMLElement | null {
  return ([...root.querySelectorAll<HTMLElement>("[data-holds]")].find((el) => visible(el) && (el.dataset.holds ?? "").split(" ").includes(name)) ?? null);
}

const FLASH_MS = 2600;

/** `smooth` false: jump instead of gliding (the user asked for reduced motion). */
export function flash(el: HTMLElement, smooth = true) {
  el.classList.remove("help-flash");
  // restart the animation if it's already flashing
  void el.offsetWidth;
  el.classList.add("help-flash");
  try {
    el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "center" });
  } catch {
    el.scrollIntoView();
  }
  window.setTimeout(() => el.classList.remove("help-flash"), FLASH_MS);
}

/** Applies a highlight directive: navigate (if it names a path) and flash the control once it's on screen. */
export async function applyHighlight(d: HighlightDirective, opts: { tries?: number; everyMs?: number } = {}): Promise<boolean> {
  const sel = targetSelector(d);
  const to = safePath(d.path);
  if (to && location.pathname + location.search !== to) navigate(to);
  if (!sel) return false;
  const tries = opts.tries ?? 16;
  const name = heldName(d);
  for (let i = 0; i < tries; i++) {
    const el = findTarget(document, sel);
    if (el) {
      flash(el);
      return true;
    }
    // v4 (V4-P2): folded away: open the fold that holds it, then look again (the fold itself flashes if the target
    // still isn't showing once it's open)
    const fold = name ? holdingFold(document, name) : null;
    if (fold && i > 0) {
      if (!fold.classList.contains("is-open") && fold.dataset.fold) setFold(fold.dataset.fold, true);
      else {
        flash(fold);
        return true;
      }
    }
    await new Promise((r) => window.setTimeout(r, opts.everyMs ?? 150));
  }
  return false;
}
